import { useNavigate } from "react-router-dom";
import { useAuth, useProfile } from "@/hooks/useAuth";
import logoSmartEfi from "@/assets/logo-smart-efi.webp";
import { useActiveOrg, OrgMembership } from "@/contexts/ActiveOrgContext";
import { useAuditLog } from "@/hooks/useAuditLog";
import { useLicence } from "@/hooks/useLicence";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Building2, Briefcase, LogOut, User, ChevronDown,
  Home, Eye, AlertTriangle, Shield, Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "./ThemeToggle";
import { ModuleMegaMenu } from "./ModuleMegaMenu";

export function AppHeader() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { activeOrg, memberships, setActiveOrgId, clearActiveOrg, isObserverMode, stopObserving } = useActiveOrg();
  const { log } = useAuditLog();
  const { daysUntilExpiry, showWarning, isExpired } = useLicence();

  const org = activeOrg?.organization;
  const isCabinet = org?.type === "cabinet";

  const initials = profile?.first_name && profile?.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? "U";

  const displayName = profile?.full_name || profile?.first_name || user?.email?.split("@")[0] || "Utilisateur";

  const handleLogout = async () => {
    await log({ action_type: "logout" });
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const handleBackToHub = () => {
    if (isObserverMode) stopObserving();
    clearActiveOrg();
    navigate("/hub");
  };

  const handleSwitchOrg = (m: OrgMembership) => {
    setActiveOrgId(m.organization_id);
    navigate("/dashboard");
  };

  const otherOrgs = memberships.filter(m => m.organization_id !== activeOrg?.organization_id);

  return (
    <header className="h-14 border-b border-border/60 bg-card/80 backdrop-blur-md flex items-center px-4 gap-3 shrink-0 z-30 sticky top-0">
      {/* Logo + Back to hub */}
      <button
        onClick={handleBackToHub}
        className="flex items-center gap-2.5 hover:opacity-80 transition-opacity shrink-0"
        title="Retour au hub"
      >
        <img src={logoSmartEfi} alt="Smart-EFI" className="w-8 h-8 rounded-lg object-cover shadow-md shadow-primary/20" />
      </button>

      {/* Separator */}
      <div className="w-px h-6 bg-border" />

      {/* Current org — with switcher dropdown */}
      {org && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 min-w-0 px-2 py-1.5 rounded-lg hover:bg-muted transition-colors">
              <div className={cn(
                "w-7 h-7 rounded-md flex items-center justify-center shrink-0",
                isCabinet ? "bg-primary/10" : "bg-accent/15"
              )}>
                {isCabinet ? <Briefcase className="w-3.5 h-3.5 text-primary" /> : <Building2 className="w-3.5 h-3.5 text-accent-foreground" />}
              </div>
              <span className="text-sm font-semibold truncate max-w-[200px]">{org.name}</span>
              {isObserverMode && (
                <Badge variant="outline" className="gap-1 text-[10px] border-warning text-warning">
                  <Eye className="w-3 h-3" /> Obs
                </Badge>
              )}
              {memberships.length > 1 && <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Organisation active</DropdownMenuLabel>
            <DropdownMenuItem className="gap-2 font-medium" disabled>
              <Check className="w-4 h-4 text-primary" />
              <span className="truncate">{org.name}</span>
              <Badge variant="outline" className="ml-auto text-[9px] capitalize">{org.type}</Badge>
            </DropdownMenuItem>

            {otherOrgs.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Changer vers</DropdownMenuLabel>
                {otherOrgs.map((m) => {
                  const o = m.organization;
                  const isC = o.type === "cabinet";
                  return (
                    <DropdownMenuItem
                      key={m.id}
                      className="gap-2 cursor-pointer"
                      onClick={() => handleSwitchOrg(m)}
                    >
                      <div className={cn(
                        "w-5 h-5 rounded flex items-center justify-center shrink-0",
                        isC ? "bg-primary/10" : "bg-accent/15"
                      )}>
                        {isC ? <Briefcase className="w-3 h-3 text-primary" /> : <Building2 className="w-3 h-3 text-accent-foreground" />}
                      </div>
                      <span className="truncate">{o.name}</span>
                      <Badge variant="outline" className="ml-auto text-[9px] capitalize">{o.type}</Badge>
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleBackToHub} className="gap-2 text-muted-foreground">
              <Home className="w-4 h-4" /> Voir toutes les organisations
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Licence warning */}
      {showWarning && !isExpired && daysUntilExpiry !== null && (
        <Badge variant="outline" className="gap-1 text-[10px] border-warning text-warning ml-2 hidden sm:flex">
          <AlertTriangle className="w-3 h-3" />
          Licence expire dans {daysUntilExpiry}j
        </Badge>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Mega menu */}
      <ModuleMegaMenu />

      {/* Theme toggle */}
      <ThemeToggle />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-colors">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500/20 to-indigo-500/20 text-primary flex items-center justify-center text-xs font-bold ring-1 ring-primary/10">
              {initials}
            </div>
            <span className="text-sm font-medium hidden sm:block max-w-[120px] truncate">{displayName}</span>
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-3 py-2">
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
            {activeOrg && (
              <Badge className="mt-1.5 text-[10px]" variant="secondary">
                {activeOrg.role === "superadmin" && <Shield className="w-3 h-3 mr-1" />}
                {activeOrg.role}
              </Badge>
            )}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/settings/profile")} className="gap-2">
            <User className="w-4 h-4" /> Mon profil
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="gap-2 text-destructive focus:text-destructive">
            <LogOut className="w-4 h-4" /> Déconnexion
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
